// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://app.worldwidecouriercargo.com/api';
const AUTH_KEY = process.env.REACT_APP_AUTH_KEY || 'nFNNFEIFLNjnNEdkfnekfnenf!kdnef?kdfn??';
const TRACKCOURIER_API_KEY = process.env.REACT_APP_TRACKCOURIER_API_KEY || 'tc_live_985EvkdJ44vr8zTuz_ifvvVxqEZw4QXyYrI6W1p5NJ0';
const TRACKCOURIER_PROXY_URL = process.env.REACT_APP_TRACKCOURIER_PROXY_URL || '/api/trackcourier.php';

/**
 * Auto-match courier slug if forward partner name is known
 */
export const detectCourierSlug = (forwardName) => {
  if (!forwardName) return null;
  const lower = forwardName.toLowerCase();
  if (lower.includes('blue') || lower.includes('dart')) return 'bluedart';
  if (lower.includes('delhiv')) return 'delhivery';
  if (lower.includes('dtdc')) return 'dtdc';
  if (lower.includes('speed')) return 'speedpost';
  if (lower.includes('india') || lower.includes('post')) return 'indiapost';
  if (lower.includes('ecom')) return 'ecomexpress';
  if (lower.includes('xpress') || lower.includes('bees')) return 'xpressbees';
  if (lower.includes('shadow')) return 'shadowfax';
  if (lower.includes('ekart')) return 'ekart';
  if (lower.includes('prof') || lower.includes('tpc')) return 'professional';
  if (lower.includes('trackon')) return 'trackon';
  if (lower.includes('maruti')) return 'shreemaruti';
  if (lower.includes('gati')) return 'gati';
  if (lower.includes('safe')) return 'safexpress';
  if (lower.includes('fedex')) return 'fedex';
  if (lower.includes('dhl')) return 'dhl';
  if (lower.includes('ups')) return 'ups';
  if (lower.includes('aramex')) return 'aramex';
  return null;
};

/**
 * Auto-detect candidate courier from tracking number pattern
 */
export const detectCourierFromNumber = (num) => {
  if (!num) return null;
  const clean = num.trim();
  if (/^[A-Za-z]{2}\d{9}[A-Za-z]{2}$/i.test(clean)) return 'speedpost';
  if (/^[A-Za-z]\d{8,9}$/i.test(clean)) return 'dtdc';
  if (/^\d{12,14}$/.test(clean)) return 'delhivery';
  if (/^\d{8,11}$/.test(clean)) return 'bluedart';
  return null;
};

/**
 * Fetch from internal World Wide Courier Cargo database
 */
const fetchInternalTracking = async (trackingNumber) => {
  try {
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };
    if (AUTH_KEY) {
      headers['x-api-key'] = AUTH_KEY;
      headers['X-API-KEY'] = AUTH_KEY;
      headers['auth_key'] = AUTH_KEY;
      headers['auth-key'] = AUTH_KEY;
    }
    const response = await fetch(`${API_BASE_URL}/tracking/${encodeURIComponent(trackingNumber)}`, {
      method: 'GET',
      headers: headers
    });
    if (!response.ok) return null;
    const data = await response.json();
    if (Array.isArray(data)) return data[0] || null;
    if (data && data.data) {
      return Array.isArray(data.data) ? (data.data[0] || null) : data.data;
    }
    return data || null;
  } catch (err) {
    return null;
  }
};

/**
 * Fetch external courier checkpoints via backend proxy with direct fallback
 */
const fetchExternalTracking = async (trackingNumber, courierSlug = null) => {
  const cleanNumber = (trackingNumber || '').trim();
  if (!cleanNumber) return null;

  // 1. Try PHP Proxy first
  try {
    const params = new URLSearchParams({ tracking_number: cleanNumber });
    if (courierSlug) params.append('courier', courierSlug);

    const proxyRes = await fetch(`${TRACKCOURIER_PROXY_URL}?${params.toString()}`, {
      method: 'GET',
      headers: { Accept: 'application/json' }
    });

    if (proxyRes.ok) {
      const json = await proxyRes.json();
      if (json && json.data && json.data.Checkpoints && json.data.Checkpoints.length > 0) {
        return json.data;
      }
    }
  } catch (err) {
    // Proxy unavailable (e.g. running in local React development server without PHP)
  }

  // 2. Direct fallback to TrackCourier.io API
  if (TRACKCOURIER_API_KEY) {
    const candidateSlugs = courierSlug
      ? [courierSlug]
      : [
          detectCourierFromNumber(cleanNumber),
          'bluedart',
          'delhivery',
          'dtdc',
          'speedpost'
        ].filter(Boolean);

    for (const slug of candidateSlugs) {
      try {
        const directRes = await fetch(
          `https://api.trackcourier.io/v1/track?courier=${encodeURIComponent(slug)}&tracking_number=${encodeURIComponent(cleanNumber)}`,
          { headers: { 'X-API-Key': TRACKCOURIER_API_KEY, Accept: 'application/json' } }
        );
        if (directRes.ok) {
          const directJson = await directRes.json();
          if (
            directJson &&
            directJson.data &&
            directJson.data.Checkpoints &&
            directJson.data.Checkpoints.length > 0 &&
            !directJson.data.CourierHasNoRecordOfShipment &&
            directJson.data.Result !== 'failure'
          ) {
            return directJson.data;
          }
        }
      } catch (e) {
        // continue
      }
    }
  }

  return null;
};

/**
 * Transform external checkpoints into standard history entries with white-labeling
 */
const formatCheckpointState = (rawState, cleanActivity) => {
  if (!rawState) {
    if (cleanActivity) {
      return cleanActivity.length > 40 ? `${cleanActivity.substring(0, 40)}...` : cleanActivity;
    }
    return 'Transit Update';
  }
  const s = rawState.toLowerCase().replace(/[^a-z]/g, '');
  if (s === 'delivered') return 'Delivered';
  if (s === 'outfordelivery') return 'Out For Delivery';
  if (s === 'intransit') return 'In Transit';
  if (s === 'pickedup') return 'Picked Up';
  if (s === 'arrived') return 'Arrived';
  if (s === 'pending') return 'Pending';
  return rawState.charAt(0).toUpperCase() + rawState.slice(1).replace(/_/g, ' ');
};

export const formatDisplayDate = (dt) => {
  if (!dt) return null;
  try {
    const d = new Date(dt);
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    }
  } catch (e) {}
  return dt;
};

/**
 * Transform external checkpoints into standard history entries with white-labeling
 */
const transformExternalCheckpoints = (checkpoints = []) => {
  return checkpoints.map((cp) => {
    let cleanActivity = (cp.Activity || '').replace(/<[^>]*>?/gm, '').replace(/https?:\/\/[^\s]+/g, '').trim();
    // Scrub competitor names to maintain World Wide Courier Cargo branding
    cleanActivity = cleanActivity.replace(/\b(delhivery|bluedart|blue dart|dtdc|ecom express|xpressbees|shadowfax|trackon)\b/gi, 'World Wide Express Hub');
    
    const statusTitle = formatCheckpointState(cp.CheckpointState, cleanActivity);

    return {
      date: cp.Date || '',
      time: cp.Time || '',
      status: statusTitle,
      remark: cleanActivity,
      location: cp.Location || '',
      courierName: cp.CourierName || ''
    };
  });
};

/**
 * Master Tracking Function
 * Seamlessly checks internal system for consignor & consignment details,
 * and integrates live history checkpoints from the third-party API.
 */
export const fetchTrackingDetails = async (trackingNumber) => {
  const cleanNumber = (trackingNumber || '').trim();
  if (!cleanNumber) {
    throw new Error('Please enter a tracking or AWB number.');
  }

  // 1. Fetch consignment details from Company API
  const internalData = await fetchInternalTracking(cleanNumber);

  // 2. Identify partner forwarding details if available
  let forwardNo = null;
  let forwardPartner = null;
  let forwardSlug = null;

  if (internalData) {
    forwardNo = internalData.forwardNo || internalData.awb_no || internalData.fwd_no || internalData.forward_no || null;
    forwardPartner = internalData.forwardName || internalData.network || internalData.fwdToBranch || internalData.fwd_name || internalData.forward_name || null;
    if (forwardPartner) {
      forwardSlug = detectCourierSlug(forwardPartner);
    }
  }

  // 3. Fetch History Details from Third-Party API (TrackCourier.io)
  let extData = null;

  // Try querying third-party API with forward number first if available
  if (forwardNo) {
    extData = await fetchExternalTracking(forwardNo, forwardSlug);
  }

  // If no forward number or no checkpoints returned, query third-party API with tracking number directly
  if (!extData || !extData.Checkpoints || extData.Checkpoints.length === 0) {
    const directExt = await fetchExternalTracking(cleanNumber, forwardSlug);
    if (directExt && directExt.Checkpoints && directExt.Checkpoints.length > 0) {
      extData = directExt;
    }
  }

  // Format Third-Party Checkpoints into unified history
  const thirdPartyHistory = extData && extData.Checkpoints && extData.Checkpoints.length > 0
    ? transformExternalCheckpoints(extData.Checkpoints)
    : [];

  // CASE A: Shipment found in Company API
  if (internalData) {
    // History should feature the live checkpoints from the third-party API
    let combinedHistory = [];
    if (thirdPartyHistory.length > 0) {
      combinedHistory = [...thirdPartyHistory];

      // If internal database has earlier booking history (e.g. Booking Created), append at the start of shipment journey
      if (Array.isArray(internalData.history) && internalData.history.length > 0) {
        const earliestInternal = internalData.history[internalData.history.length - 1];
        const isDuplicate = combinedHistory.some((h) =>
          (h.status || '').toLowerCase() === (earliestInternal.status || '').toLowerCase() &&
          h.date === earliestInternal.date
        );
        if (!isDuplicate && earliestInternal.status) {
          combinedHistory.push(earliestInternal);
        }
      }
    } else if (Array.isArray(internalData.history) && internalData.history.length > 0) {
      combinedHistory = [...internalData.history];
    }

    const latestStatus = (thirdPartyHistory.length > 0 && (extData?.MostRecentStatus || extData?.ShipmentState || thirdPartyHistory[0]?.status))
      || internalData.status
      || internalData.dl_status
      || 'In Transit';

    const latestLocation = thirdPartyHistory[0]?.location || internalData.destination || 'In Transit';

    return {
      number: internalData.awb_no || internalData.awbNo || internalData.number || internalData.cnNo || cleanNumber,
      status: latestStatus,
      origin: internalData.origin || internalData.consignorCity || internalData.cln_city || internalData.from || 'N/A',
      destination: internalData.destination || internalData.consigneeCity || internalData.con_city || internalData.to || 'N/A',
      currentLocation: latestLocation,
      estimatedDelivery: extData?.EstimatedDelivery || internalData.estimatedDelivery || null,
      carrier: 'World Wide Courier Cargo',
      weight: internalData.weight || (internalData.total_actual_weight ? `${internalData.total_actual_weight} KG` : (internalData.wt || 'N/A')),
      dimensions: internalData.dimensions || (internalData.length && internalData.width && internalData.height ? `${internalData.length}x${internalData.width}x${internalData.height} ${internalData.size || 'cm'}` : 'N/A'),
      cnNo: internalData.awb_no || internalData.cnNo || internalData.number || cleanNumber,
      cnDate: internalData.awb_dt || internalData.cnDate || internalData.awbDate || internalData.bookingDate || null,
      bookingDate: formatDisplayDate(internalData.awb_dt || internalData.bookingDate || internalData.cnDate || internalData.awbDate),

      // Consignor & Consignee details from Company API
      consignorName: internalData.consignorName || internalData.consignor || internalData.cln_name || internalData.shipperName || internalData.shp_name || internalData.senderName || 'N/A',
      consignorCity: internalData.consignorCity || internalData.cln_city || internalData.origin || '',
      consignorAddress: internalData.consignorAddress || internalData.cln_add || '',
      consignorPin: internalData.consignorPin || internalData.cln_pin || '',
      consignee: internalData.consigneeName || internalData.consignee || internalData.con_name || internalData.receiverName || 'N/A',
      consigneeCity: internalData.consigneeCity || internalData.con_city || internalData.destination || '',
      consigneeAddress: internalData.consigneeAddress || internalData.con_add || '',
      consigneePin: internalData.consigneePin || internalData.con_pin || '',
      noOfBox: internalData.pcs || internalData.noOfBox || internalData.pieces || '1',
      mode: internalData.mode || internalData.mode_type || '',
      network: internalData.network || forwardPartner || '',

      forwardPartner: forwardPartner,
      forwardNo: forwardNo,

      podImage: internalData.podImage || internalData.pod_img || extData?.PODImageUrl || null,
      statusDate: thirdPartyHistory[0]?.date || internalData.statusDate || internalData.dl_statusdate || null,
      statusTime: thirdPartyHistory[0]?.time || internalData.statusTime || internalData.dl_statustime || null,
      statusReceiver: internalData.statusReceiver || internalData.dl_statusrec || 'N/A',
      statusRemark: thirdPartyHistory[0]?.remark || internalData.statusRemark || internalData.dl_statusremk || '',

      // History details from third-party API
      history: combinedHistory,
      hasThirdPartyHistory: thirdPartyHistory.length > 0
    };
  }

  // CASE B: Consignment not found in Company API, but found in Third-Party API
  if (thirdPartyHistory.length > 0) {
    return {
      number: cleanNumber,
      status: extData.MostRecentStatus || extData.ShipmentState || thirdPartyHistory[0]?.status || 'In Transit',
      origin: extData.Origin || 'N/A',
      destination: extData.Destination || 'N/A',
      currentLocation: thirdPartyHistory[0]?.location || 'In Transit',
      estimatedDelivery: extData.EstimatedDelivery || null,
      carrier: 'World Wide Courier Cargo',
      cnNo: cleanNumber,
      cnDate: thirdPartyHistory[thirdPartyHistory.length - 1]?.date || null,
      consignorName: 'N/A',
      consignee: 'N/A',
      noOfBox: '1',
      statusDate: thirdPartyHistory[0]?.date || null,
      statusTime: thirdPartyHistory[0]?.time || null,
      statusRemark: thirdPartyHistory[0]?.remark || '',
      podImage: extData.PODImageUrl || null,
      history: thirdPartyHistory,
      hasThirdPartyHistory: true
    };
  }

  throw new Error(`No consignment record found for tracking number #${cleanNumber}. Please check the number and try again.`);
};
